import React, { useState, useRef } from 'react'
import { Icon } from '@iconify/react'
import bellFill from '@iconify/icons-eva/bell-fill'
import bellOutline from '@iconify/icons-eva/bell-outline'
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Tooltip,
  Avatar,
  useTheme,
} from '@material-ui/core'
import { alpha } from '@material-ui/core/styles'
import MenuPopover from 'src/components/MenuPopover'
import { formatDistanceToNow } from 'date-fns'
import { useNotifications, useMarkNotificationsAsRead } from '../../api/user/list'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteIcon from '@material-ui/icons/Delete'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount'
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore'

const MAX_NOTIFICATIONS = 10

// Status of each operation_type, read from the palette so the popover matches
// the status colours used in tables and snackbars.
const OPERATION_STATUS = {
  1: 'success', // Marked as finished
  8: 'success', // Analysis finished successfully
  10: 'success', // Balance increased
  2: 'warning', // Marked as unfinished
  13: 'warning', // Gave admin permission
  14: 'warning', // Remove admin permission
  3: 'info', // Uploaded a fastq
  4: 'info', // Uploaded a vcf
  12: 'info', // Added user
  5: 'error', // Deleted a fastq
  6: 'error', // Deleted a vcf
  9: 'error', // Analysis failed
  11: 'error', // Insufficient balance
  7: 'primary', // Started analysis
}

// Icon tone per status that keeps at least 3:1 against paper in both modes.
const getStatusTone = (theme, operationType) => {
  const status = OPERATION_STATUS[operationType]
  const isLight = theme.palette.mode === 'light'
  if (!status) return theme.palette.text.secondary
  if (status === 'primary') return theme.palette.primary.main
  if (status === 'warning') return isLight ? theme.palette.warning.dark : theme.palette.warning.main
  return isLight ? theme.palette[status].main : theme.palette[status].light
}

const getNotificationIcon = (operationType) => {
  switch (operationType) {
    case 1: // Marked as finished
    case 8: // Analysis finished successfully
      return <CheckCircleOutlineIcon />
    case 2: // Marked as unfinished
      return <ErrorOutlineIcon />
    case 3: // Uploaded a fastq
    case 4: // Uploaded a vcf
      return <CloudUploadIcon />
    case 5: // Deleted a fastq
    case 6: // Deleted a vcf
      return <DeleteIcon />
    case 7: // Started analysis
      return <SettingsBackupRestoreIcon />
    case 9: // Analysis failed
    case 11: // Insufficient balance
      return <ErrorOutlineIcon />
    case 10: // Balance increased
      return <AccountBalanceWalletIcon />
    case 12: // Added user
      return <PersonAddIcon />
    case 13: // Gave admin permission
    case 14: // Remove admin permission
      return <SupervisorAccountIcon />
    default:
      return <Icon icon={bellFill} width={20} height={20} />
  }
}

export default function NotificationPopover() {
  const theme = useTheme()
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const { unreadLogs: rawUnreadLogs, latestLogs: rawLatestLogs, unreadCount } = useNotifications()

  // Both are rendered with .slice().map(), so a non-array response throws inside
  // render. This component sits in the dashboard layout, above every route, so
  // that throw blanks the entire application rather than just the bell icon.
  const latestLogs = Array.isArray(rawLatestLogs) ? rawLatestLogs : []
  const unreadLogs = Array.isArray(rawUnreadLogs) ? rawUnreadLogs : []
  const markAsReadMutation = useMarkNotificationsAsRead()

  // Optimistic unread count state
  const [optimisticUnreadCount, setOptimisticUnreadCount] = useState(unreadCount)

  // Sync optimisticUnreadCount with unreadCount from hook
  React.useEffect(() => {
    setOptimisticUnreadCount(unreadCount)
  }, [unreadCount])

  const handleOpen = async () => {
    setOpen(true)
    if (unreadLogs.length > 0) {
      setOptimisticUnreadCount(0) // Optimistically set to 0 on open
      const latestLogId = unreadLogs[0].id
      try {
        await markAsReadMutation.mutateAsync(latestLogId)
      } catch (error) {
        console.error('Error marking notifications as read:', error)
        setOptimisticUnreadCount(unreadCount)
      }
    } else {
      setOptimisticUnreadCount(unreadCount)
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const visibleLogs = latestLogs.slice(0, MAX_NOTIFICATIONS)

  const renderContent = (
    <MenuPopover
      open={open}
      onClose={handleClose}
      anchorEl={anchorRef.current}
      sx={{
        width: 360,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">Notifications</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {optimisticUnreadCount || 0} unread
        </Typography>
      </Box>

      <Divider />

      {visibleLogs.length === 0 ? (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <Box component={Icon} icon={bellOutline} sx={{ width: 28, height: 28, mb: 1, color: 'text.secondary' }} />
          <Typography variant="subtitle2">No notifications</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Uploads, analyses and account changes will show up here.
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {visibleLogs.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              isUnread={unreadLogs.some((log) => log.id === notification.id)}
            />
          ))}
        </List>
      )}

      <Divider />

      <Box sx={{ p: 1 }}>
        <Button
          fullWidth
          size="small"
          component="a"
          href="/libra/user/list"
          sx={{
            color: 'text.primary',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.16),
            },
          }}
        >
          View all
        </Button>
      </Box>
    </MenuPopover>
  )

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          ref={anchorRef}
          onClick={handleOpen}
          aria-label="Notifications"
          sx={{
            width: 36,
            height: 36,
            p: 0,
            borderRadius: 1,
            color: 'action.active',
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
            ...(open && { color: 'text.primary', bgcolor: 'action.selected' }),
          }}
        >
          <Badge badgeContent={optimisticUnreadCount} color="error">
            <Icon icon={bellFill} width={20} height={20} />
          </Badge>
        </IconButton>
      </Tooltip>

      {renderContent}
    </>
  )
}

function NotificationItem({ notification, isUnread }) {
  const theme = useTheme()
  const { operation_type_display, file_name, created_at, person_name, operation_type } = notification
  const tone = getStatusTone(theme, operation_type)
  const title = `${operation_type_display || ''}${file_name ? `: ${file_name}` : ''}`

  return (
    <ListItemButton
      sx={{
        py: 1.25,
        pl: 2.25,
        pr: 2.5,
        alignItems: 'flex-start',
        backgroundColor: isUnread ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
        borderLeft: `3px solid ${isUnread ? theme.palette.primary.main : 'transparent'}`,
        '&:hover': {
          backgroundColor: isUnread ? alpha(theme.palette.primary.main, 0.1) : theme.palette.action.hover,
        },
      }}
    >
      <Avatar
        sx={{
          mr: 1.5,
          mt: 0.25,
          width: 32,
          height: 32,
          color: tone,
          bgcolor: alpha(tone, 0.12),
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        {getNotificationIcon(operation_type)}
      </Avatar>
      <ListItemText
        sx={{ my: 0 }}
        primary={
          <Typography
            variant="body2"
            noWrap
            title={title}
            sx={{
              fontWeight: isUnread ? 600 : 500,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
        }
        secondary={
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 0.25,
            }}
          >
            <Typography component="span" variant="caption" noWrap sx={{ color: 'text.secondary' }}>
              {person_name}
            </Typography>
            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
              {formatDistanceToNow(new Date(created_at))} ago
            </Typography>
          </Box>
        }
      />
    </ListItemButton>
  )
}
